<template>
  <div class="fin-panel-body">
    <finance-state v-if="loading" kind="loading" :lines="2" />
    <template v-else>
      <finance-statement v-if="breakdown" :overview="breakdown" clickable @line="openLines" />
      <div v-else-if="row.kind !== 'payment'" class="ui-banner ui-banner--info ">
        <v-icon size="16" color="currentColor">mdi-information-outline</v-icon>
        <span>{{ row.from ? $t("finance_breakdown_unavailable") : $t("finance_shell.settle.legacy") }}</span>
      </div>

      <h4 class="fin-h4">{{ $t("finance_payments_for_settlement") }}</h4>
      <div v-if="status === 'estimate'" class="ui-banner ui-banner--info ">
        <v-icon size="16" color="currentColor">mdi-information-outline</v-icon><span>{{ $t("finance_shell.settle.estimate_note") }}</span>
      </div>
      <template v-else>
        <dl class="fin-kv">
          <template v-if="row.kind === 'payout'">
            <dt>{{ $t("finance_expected") }}</dt><dd class="fin-num">{{ money(row.net) }}</dd>
            <dt>{{ $t("finance_paid") }}</dt><dd class="fin-num">{{ money(row.paid) }}</dd>
            <dt>{{ $t("finance_open_amount") }}</dt><dd class="fin-num"><b>{{ money(row.remaining) }}</b></dd>
          </template>
          <dt v-else>{{ $t("finance_paid") }}</dt><dd v-if="row.kind !== 'payout'" class="fin-num">{{ money(row.paid) }}</dd>
          <template v-if="row.date"><dt>{{ $t("finance_date") }}</dt><dd>{{ localeDate(row.date) }}</dd></template>
          <template v-if="row.method"><dt>{{ $t("finance_payment_method_label") }}</dt><dd>{{ methodText }}</dd></template>
          <template v-if="row.reference"><dt>{{ $t("finance_reference") }}</dt><dd>{{ row.reference }}</dd></template>
          <template v-if="row.note"><dt>{{ $t("finance_shell.settle.note") }}</dt><dd>{{ row.note }}</dd></template>
          <template v-if="row.createdBy"><dt>{{ $t("finance_recorded_by") }}</dt><dd>{{ row.createdBy }}</dd></template>
        </dl>
        <template v-if="status === 'partial'">
          <div class="fin-progress" role="img" :aria-label="`${percent} %`"><i :style="{ width: `${percent}%` }"></i></div>
          <small class="ui-muted">{{ $t("finance_shell.settle.paid_percent", { percent }) }}</small>
        </template>
        <p v-if="status === 'pending'" class="ui-muted">{{ $t("finance_no_payments_yet") }}</p>
      </template>

      <div class="fin-actions">
        <button v-if="status !== 'estimate'" type="button" class="ui-btn" @click="shell.printPanel"><v-icon size="16">mdi-printer-outline</v-icon>{{ $t("finance_download_pdf") }}</button>
        <a class="ui-btn" :href="reportMailto"><v-icon size="16">mdi-flag-outline</v-icon>{{ $t("finance_report_error") }}</a>
      </div>
    </template>
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import { formatMoney } from "@/components/restaurant/finance/constants";
import { parseOverview } from "@/components/restaurant/finance/overview";
import FinanceState from "../parts/FinanceState.vue";
import FinanceStatement from "../parts/FinanceStatement.vue";
import LinesPanel from "./LinesPanel.vue";
import { paidPercent, settlementStatus } from "../financeLogic";

/**
 * Detalj obračuna: razrada za njegov period (redovi se otvaraju u panelu), plaćeno / otvoreno, način i referenca.
 * Procjena tekućeg perioda nosi svoju razradu; zaključeni obračun je učitava po periodu; stariji zapisi bez datuma
 * perioda nemaju razradu. PDF je štampa samo ovog panela do Settlement faze.
 */
export default {
  name: "FinanceSettlePanel",
  components: { FinanceState, FinanceStatement },
  inject: { shell: "reportShell" },
  props: {
    row: { type: Object, required: true },
    restaurantId: { type: [Number, String], default: null },
    currency: { type: String, default: "KM" },
  },
  data: () => ({ loading: false, overview: null }),
  computed: {
    status() {
      return settlementStatus(this.row);
    },
    percent() {
      return paidPercent(this.row);
    },
    breakdown() {
      return this.row.kind === "estimate" ? this.row.overview : this.overview;
    },
    methodText() {
      const key = `finance_shell.method.${this.row.method}`;
      return this.$te(key) ? this.$t(key) : this.row.method;
    },
    reportMailto() {
      const subject = encodeURIComponent(`${this.$t("finance_report_error")}: ${this.row.periodLabel || this.row.from || ""}`);
      return `mailto:help@ordera.app?subject=${subject}`;
    },
  },
  created() {
    this.load();
  },
  methods: {
    money(value) {
      return formatMoney(value, this.currency);
    },
    async load() {
      if (this.row.kind !== "payout" || !this.row.from || !this.row.to || !this.restaurantId) {
        return;
      }
      this.loading = true;
      try {
        const res = await restaurantFinanceService.getFinancialOverview(this.restaurantId, { from: this.row.from, to: this.row.to });
        this.overview = parseOverview((res.data && res.data.data) || res.data || {}, this.currency);
      } catch (error) {
        this.overview = null;
      } finally {
        this.loading = false;
      }
    },
    openLines(line) {
      this.shell.openPanel({
        key: `lines-${line.key}-${this.row.from}-${this.row.to}`,
        title: this.translate(line.label),
        subtitle: `${this.localeDate(this.row.from)} – ${this.localeDate(this.row.to)}`,
        component: LinesPanel,
        props: { restaurantId: this.restaurantId, category: line.key, from: this.row.from, to: this.row.to, currency: this.currency },
      });
    },
  },
};
</script>
